import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Optional, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AusstallenService } from '../../../../../projects/kiene-core/src/lib/components/ausstallen/ausstallen.service';
import { Client } from '../../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { ClientService } from '../../../../../projects/kiene-core/src/lib/components/client/client.service';
import {
    ConfirmDialogInput,
    ConfirmDialogResult,
} from '../../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog.component';
import {
    ohneTierlisteKommentar,
    Tierliste,
} from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/tierliste';
import {
    LISTE_ABSCHLIESSEN_STEPPER_HANDLER,
    ListeAbschliessenStepperHandler,
} from '../../../../../projects/kiene-core/src/lib/components/gruppen/gruppe-abgang/liste-abschliessen-stepper.handler';
import { Gruppe } from '../../../../../projects/kiene-core/src/lib/components/gruppen/classes/gruppe';
import { ImportOptionenDialogComponent } from '../../../../../projects/kiene-core/src/lib/components/gruppen/import-optionen-dialog/import-optionen-dialog.component';
import { GruppenService } from '../../../../../projects/kiene-core/src/lib/components/gruppen/gruppen.service';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { AppService } from '../../../../../projects/kiene-core/src/lib/services/app/app.service';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneUtils } from '../../../../../projects/kiene-core/src/lib/utils/KieneUtils';
import { ChangeCurrentClientService } from '../../../administration/client/change-current-client/change-current-client.service';
import { GruppeDialogInput } from '../../../documents/gruppenverwaltung/gruppenuebersicht/gruppe-dialog/gruppe-dialog';
import { GruppeDialogComponent } from '../../../documents/gruppenverwaltung/gruppenuebersicht/gruppe-dialog/gruppe-dialog.component';
import { GruppeAuswaehlenDialogComponent } from '../../tierlisten/aktuelle-tierlisten/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog.component';
import { GruppeAuswaehlenDialogInput2 } from '../../tierlisten/aktuelle-tierlisten/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog';
import { getGruppeAuswaehlenDialogShellConfig } from '../../../../../projects/kiene-core/src/lib/components/gruppen/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog';
import { HIT_LISTE_WIEDER_OEFFNEN_FEHLER } from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/hit-meldung-status.util';
import { isTierlisteClosed } from './letzte-aktionen.models';
import { LetzteAktionenTierlisteMenuCacheService } from './letzte-aktionen-tierliste-menu-cache.service';
import { GruppenDashboardImportRefreshService } from '../gruppen-dashboard-import-refresh.service';

export type LetzteAktionenTierlisteMenuMode = 'ausstallen' | 'abgeschlossen' | 'importieren' | 'scans_fortsetzen';

/** Same permission IDs as Meine Tierlisten / Abgänge table options. */
const PERMISSION_WIEDER_OEFFNEN = 9323;
const PERMISSION_DOWNLOAD = 1971;
const PERMISSION_SCHLACHTHOF = 9326;
const PERMISSION_IMPORTIEREN = 9322;
const PERMISSION_NICHT_IMPORTIEREN = 9323;
const PERMISSION_TIERLISTE_LOESCHEN = 9324;

const STATUS_IMPORTIERT = 163;

@Component({
    selector: 'kiene-letzte-aktionen-tierliste-menu',
    templateUrl: './letzte-aktionen-tierliste-menu.component.html',
    styleUrls: ['./letzte-aktionen-tierliste-menu.component.scss'],
})
export class LetzteAktionenTierlisteMenuComponent implements OnChanges, OnDestroy {
    @Input() tierlisteId: number;
    @Input() mode: LetzteAktionenTierlisteMenuMode = 'ausstallen';
    /** Dashboard row action — wieder öffnen applies to Ausstallen lists only. */
    @Input() menuAktion: string | null = null;
    /** From dashboard row when available; avoids waiting for read_one. */
    @Input() listenstatusId: number | null = null;
    @Output() changed = new EventEmitter<void>();
    @Output() continueScans = new EventEmitter<void>();
    @Output() directScanListeAbschliessen = new EventEmitter<void>();

    tierliste: Tierliste | null = null;
    loading = false;

    private loadSubscription: Subscription | null = null;

    constructor(
        private readonly menuCache: LetzteAktionenTierlisteMenuCacheService,
        private readonly dialog: MatDialog,
        private readonly messageService: MessageService,
        private readonly localStorageService: LocalStorageService,
        private readonly ausstallenService: AusstallenService,
        private readonly clientService: ClientService,
        private readonly changeCurrentClientService: ChangeCurrentClientService,
        private readonly appService: AppService,
        private readonly gruppenService: GruppenService,
        private readonly api: KieneBackendApiService,
        private readonly gruppenDashboardImportRefresh: GruppenDashboardImportRefreshService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        @Optional()
        @Inject(LISTE_ABSCHLIESSEN_STEPPER_HANDLER)
        private readonly listeAbschliessenStepperHandler?: ListeAbschliessenStepperHandler,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tierlisteId']) {
            this.bindTierliste(this.tierlisteId);
        }
    }

    ngOnDestroy(): void {
        this.loadSubscription?.unsubscribe();
    }

    onTriggerHover(): void {
        if (this.isScansFortsetzenMode || this.menuDisabled || (!this.loading && this.tierliste?.tierliste_id === this.tierlisteId)) {
            return;
        }
        this.bindTierliste(this.tierlisteId);
    }

    get isScansFortsetzenMode(): boolean {
        return this.mode === 'scans_fortsetzen';
    }

    get isAusstallenMode(): boolean {
        return this.mode === 'ausstallen';
    }

    get isAbgeschlossenMode(): boolean {
        return this.mode === 'abgeschlossen';
    }

    get isImportierenMode(): boolean {
        return this.mode === 'importieren';
    }

    onScansFortsetzen(): void {
        if (this.menuDisabled || !this.isScansFortsetzenMode) {
            return;
        }
        this.continueScans.emit();
    }

    onListeAbschliessen(): void {
        if (this.menuDisabled || !this.isScansFortsetzenMode) {
            return;
        }
        this.directScanListeAbschliessen.emit();
    }

    get menuReady(): boolean {
        if (this.isScansFortsetzenMode) {
            return !!this.tierlisteId && this.tierlisteId > 0;
        }
        return !!this.tierliste && !this.loading;
    }

    /** Prefer loaded tierliste status; fall back to dashboard row. */
    get effectiveStatusId(): number | null {
        if (this.tierliste?.status_id != null) {
            return this.tierliste.status_id;
        }
        return this.listenstatusId;
    }

    /** importieren: enabled when tierliste exists. ausstallen: closed (162+). abgeschlossen: still open (< 162). */
    get menuDisabled(): boolean {
        if (!this.tierlisteId || this.tierlisteId <= 0) {
            return true;
        }
        if (this.isScansFortsetzenMode) {
            return false;
        }
        if (this.isImportierenMode) {
            return false;
        }
        const statusId = this.effectiveStatusId;
        if (statusId == null) {
            return this.isAbgeschlossenMode;
        }
        if (this.isAusstallenMode) {
            return isTierlisteClosed(statusId);
        }
        return !isTierlisteClosed(statusId);
    }

    get canWiederOeffnen(): boolean {
        return this.localStorageService.hasPermission(PERMISSION_WIEDER_OEFFNEN);
    }

    /** Re-open is only for closed Ausstall lists (162 → 161), not Einstallen / importiert (163). */
    get showWiederOeffnenMenuItem(): boolean {
        return this.canWiederOeffnen && this.isAbgeschlossenMode && this.menuAktion === 'ausstallen';
    }

    get canDownload(): boolean {
        return this.localStorageService.hasPermission(PERMISSION_DOWNLOAD);
    }

    get canSchlachthofSenden(): boolean {
        return this.localStorageService.hasPermission(PERMISSION_SCHLACHTHOF);
    }

    get canImportieren(): boolean {
        return this.localStorageService.hasPermission(PERMISSION_IMPORTIEREN);
    }

    get canNichtImportieren(): boolean {
        return this.localStorageService.hasPermission(PERMISSION_NICHT_IMPORTIEREN);
    }

    get canTierlisteLoeschen(): boolean {
        return this.localStorageService.hasPermission(PERMISSION_TIERLISTE_LOESCHEN);
    }

    get hasPrimaryAction(): boolean {
        if (!this.tierlisteId || this.tierlisteId <= 0 || this.menuDisabled) {
            return false;
        }
        if (this.isAusstallenMode) {
            return true;
        }
        if (this.isAbgeschlossenMode) {
            return this.canDownload;
        }
        return this.canImportieren;
    }

    get scansFortsetzenDisabled(): boolean {
        return this.menuDisabled;
    }

    /** Same rules as Abgänge: Ausstallen only, status exactly 162, backend flag darf_wieder_oeffnen. */
    get wiederOeffnenDisabled(): boolean {
        if (!this.showWiederOeffnenMenuItem || this.menuDisabled || this.loading) {
            return true;
        }
        const rowStatus = this.listenstatusId != null ? Number(this.listenstatusId) : null;
        if (rowStatus != null && rowStatus >= STATUS_IMPORTIERT) {
            return true;
        }
        if (this.tierliste != null) {
            const tlStatus = Number(this.tierliste.status_id) || 0;
            if (tlStatus >= STATUS_IMPORTIERT) {
                return true;
            }
            return !KieneUtils.isApiOne(this.tierliste.darf_wieder_oeffnen);
        }
        return rowStatus !== 162;
    }

    tierlisteWiederOeffnen(): void {
        if (this.wiederOeffnenDisabled) {
            return;
        }
        this.runWithTierliste(tierliste => this.openWiederOeffnenDialog(tierliste), { requireReopenable: true });
    }

    downloadTierliste(): void {
        this.runWithTierliste(tierliste => {
            this.gruppenService.openGruppeAbgangDownloadDialog({ tierliste });
        });
    }

    tierlisteAnSchlachthof(): void {
        this.runWithTierliste(tierliste => {
            this.ausstallenService.tierlisteAnSchlachthofSenden(tierliste).subscribe({
                next: (result: boolean) => {
                    if (result === true) {
                        this.emitChanged();
                    }
                },
            });
        });
    }

    scansFortsetzen(): void {
        if (this.scansFortsetzenDisabled) {
            return;
        }
        this.runWithTierliste(tierliste => this.openAbgangStepper(tierliste, 'tiere'));
    }

    listeAbschliessen(): void {
        if (this.scansFortsetzenDisabled) {
            return;
        }
        this.runWithTierliste(tierliste => this.openAbgangStepper(tierliste, 'ausstallen'));
    }

    tierlisteLoeschen(): void {
        if (!this.canTierlisteLoeschen || this.loading) {
            return;
        }
        this.runWithTierliste(tierliste => this.openTierlisteLoeschenDialog(tierliste));
    }

    zuNeuerGruppeHinzufuegen(): void {
        if (!this.canImportieren) {
            return;
        }
        this.runWithTierliste(tierliste => this.resolveImportClient(tierliste, kunde => this.tierlisteInNeueGruppe(tierliste, kunde)));
    }

    zuVorhandenerGruppeHinzufuegen(): void {
        if (!this.canImportieren) {
            return;
        }
        this.runWithTierliste(tierliste => this.resolveImportClient(tierliste, kunde => this.tierlisteInAlteGruppe(tierliste, kunde)));
    }

    inGruppeImportieren(): void {
        this.zuVorhandenerGruppeHinzufuegen();
    }

    nichtImportieren(): void {
        if (!this.canNichtImportieren) {
            return;
        }
        this.runWithTierliste(tierliste => this.openNichtImportierenDialog(tierliste));
    }

    openPrimaryAction(): void {
        if (!this.hasPrimaryAction) {
            return;
        }
        if (this.isAusstallenMode) {
            this.scansFortsetzen();
            return;
        }
        if (this.isAbgeschlossenMode) {
            this.downloadTierliste();
            return;
        }
        this.zuVorhandenerGruppeHinzufuegen();
    }

    private openWiederOeffnenDialog(tierliste: Tierliste): void {
        const tlStatus = Number(tierliste.status_id) || 0;
        if (tlStatus !== 162 || !KieneUtils.isApiOne(tierliste.darf_wieder_oeffnen)) {
            return;
        }
        const confirmInput = new ConfirmDialogInput();
        confirmInput.headline = 'Tierliste wieder öffnen';
        confirmInput.text = 'Wollen Sie die Tierliste wieder öffnen?';
        confirmInput.okButton = 'wieder öffnen';
        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmInput });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.ok && result?.confirmed) {
                    const statusAlt = tierliste.status_id;
                    tierliste.status_id = 161;
                    this.api.post(
                        this.apiBaseUrl + 'tierliste/update_kundenportal.php',
                        ohneTierlisteKommentar(tierliste),
                    ).subscribe({
                        next: response => {
                            if (response) {
                                this.emitChanged();
                            }
                        },
                        error: err => {
                            tierliste.status_id = statusAlt;
                            this.messageService.errorMessage(HIT_LISTE_WIEDER_OEFFNEN_FEHLER, err);
                        },
                    });
                }
            },
        });
    }

    private openAbgangStepper(tierliste: Tierliste, initialStep: 'tiere' | 'ausstallen'): void {
        if (!tierliste?.tierliste_id || isTierlisteClosed(tierliste.status_id)) {
            return;
        }
        if (this.listeAbschliessenStepperHandler) {
            this.listeAbschliessenStepperHandler.open(tierliste, { initialStep }).subscribe({
                next: saved => {
                    if (saved) {
                        this.emitChanged();
                    }
                },
            });
        }
    }

    private openNichtImportierenDialog(tierliste: Tierliste): void {
        const input = new ConfirmDialogInput();
        input.headline = 'Liste löschen bzw. nicht importieren?';
        input.text = 'Möchten Sie die Liste löschen bzw. nicht importieren?';
        input.okButton = 'Löschen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { width: '50%', minWidth: '300px', data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (!result?.confirmed) {
                    return;
                }
                tierliste.import_einstallen = 0;
                this.api.updateGeneric(
                    this.apiBaseUrl + 'tierliste/update_kundenportal.php',
                    ohneTierlisteKommentar(tierliste),
                ).subscribe({
                    next: () => {
                        this.messageService.infoMessage('Tierliste erfolgreich gelöscht!');
                        this.emitChanged();
                    },
                    error: err => {
                        this.messageService.errorMessage('Tierliste konnte nicht gelöscht werden!', err);
                    },
                });
            },
        });
    }

    private openTierlisteLoeschenDialog(tierliste: Tierliste): void {
        const input = new ConfirmDialogInput();
        input.headline = 'Tierliste löschen?';
        input.text = 'Wollen Sie die Tierliste wirklich löschen?';
        input.okButton = 'Löschen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { width: '500px', data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (!result?.confirmed) {
                    return;
                }
                this.api.post(this.apiBaseUrl + 'tierliste/delete_kundenportal.php', tierliste, undefined).subscribe({
                    next: () => {
                        this.emitChanged();
                    },
                    error: err => {
                        this.messageService.errorMessage('Tierliste konnte nicht gelöscht werden!', err);
                    },
                });
            },
        });
    }

    private resolveImportClient(tierliste: Tierliste, onClient: (kunde: Client) => void): void {
        if (tierliste?.kunde_id_einstallen) {
            const params = new HttpParams().set('kunde_id', String(tierliste.kunde_id_einstallen));
            this.api.get(this.apiBaseUrl + 'kunde/read_one_kundenportal.php', params).subscribe({
                next: (kunde: Client) => {
                    if (kunde) {
                        onClient(kunde);
                    }
                },
                error: err => {
                    this.messageService.errorMessage('Fehler', err);
                },
            });
            return;
        }

        const currentClient = this.localStorageService.getCurrentClient();
        if (currentClient) {
            onClient(currentClient);
            return;
        }

        this.changeCurrentClientService.openChangeDialog().subscribe({
            next: (client: Client) => {
                if (client) {
                    onClient(client);
                }
            },
        });
    }

    private tierlisteInAlteGruppe(tierliste: Tierliste, kunde: Client): void {
        const input = new GruppeAuswaehlenDialogInput2();
        input.client = kunde;
        this.dialog
            .open<GruppeAuswaehlenDialogComponent, GruppeAuswaehlenDialogInput2, Gruppe>(
                GruppeAuswaehlenDialogComponent,
                { ...getGruppeAuswaehlenDialogShellConfig(), data: input },
            )
            .afterClosed()
            .subscribe({
                next: gruppe => {
                    if (gruppe) {
                        this.tierlisteInGruppe(tierliste, gruppe);
                    }
                },
            });
    }

    private tierlisteInNeueGruppe(tierliste: Tierliste, kunde: Client): void {
        this.clientService.pruefeZugriff(tierliste?.kunde_id_abgang, kunde?.kunde_id).subscribe({
            next: (zugriff: boolean) => {
                const input = new GruppeDialogInput(kunde, true, zugriff ? tierliste.gruppe_abgang : undefined);
                this.dialog
                    .open<GruppeDialogComponent, GruppeDialogInput, Gruppe>(GruppeDialogComponent, {
                        data: input,
                        minWidth: '33vw',
                    })
                    .afterClosed()
                    .subscribe({
                        next: (gruppe: Gruppe) => {
                            if (gruppe) {
                                this.tierlisteInGruppe(tierliste, gruppe);
                            }
                        },
                    });
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    private tierlisteInGruppe(tierliste: Tierliste, gruppe: Gruppe): void {
        tierliste.gruppe_id_einstallen = gruppe.gruppe_id;
        tierliste.status_id = 163;
        const optionenDialog = this.dialog.open(ImportOptionenDialogComponent, {
            maxWidth: '600px',
            data: { tierliste },
        });
        optionenDialog.afterClosed().subscribe({
            next: updatedTierliste => {
                if (!updatedTierliste) {
                    this.emitChanged();
                    return;
                }
                this.api.post(
                    this.apiBaseUrl + 'tierliste/update_kundenportal.php',
                    ohneTierlisteKommentar(updatedTierliste),
                    undefined,
                ).subscribe({
                    next: () => {
                        const importedTierlisteId = Number(updatedTierliste?.tierliste_id || tierliste?.tierliste_id || 0);
                        void this.persistImportedEinstallgewichte(updatedTierliste).finally(() => {
                            this.appService.setBlockOnLoading(false);
                            this.messageService.infoMessage('Die Tiere wurden erfolgreich der Gruppe hinzugefügt!');
                            this.gruppenDashboardImportRefresh.notifyImportCompleted(importedTierlisteId);
                            this.emitChanged();
                        });
                    },
                    error: err => {
                        this.appService.setBlockOnLoading(false);
                        this.messageService.errorMessage('Fehler!', err);
                    },
                });
            },
        });
    }

    private async persistImportedEinstallgewichte(tierliste: Tierliste): Promise<void> {
        const records = tierliste?.import_einstallgewichte_records || [];
        const gruppeId = Number(tierliste?.gruppe_id_einstallen);
        if (!records.length || gruppeId <= 0) {
            return;
        }
        try {
            await this.api
                .post(this.apiBaseUrl + 'gruppe_standort/save_einstallgewichte.php', {
                    gs_id: 0,
                    gruppe_id: gruppeId,
                    tierliste_id: Number(tierliste?.tierliste_id || 0),
                    records,
                })
                .toPromise();
        } catch (error) {
            this.messageService.errorMessage(
                'Import erfolgreich, aber Einstallgewichte konnten nicht vollstaendig gespeichert werden!',
                error,
            );
        }
    }

    private runWithTierliste(
        action: (tierliste: Tierliste) => void,
        options?: { requireReopenable?: boolean },
    ): void {
        if (this.menuDisabled) {
            return;
        }
        if (this.tierliste?.tierliste_id === this.tierlisteId) {
            if (this.canRunAction(this.tierliste, options)) {
                action(this.tierliste);
            }
            return;
        }
        this.loading = true;
        this.menuCache.get(this.tierlisteId).subscribe({
            next: tierliste => {
                this.tierliste = tierliste;
                this.loading = false;
                if (!tierliste?.tierliste_id || !this.canRunAction(tierliste, options)) {
                    return;
                }
                action(tierliste);
            },
            error: err => {
                this.loading = false;
                this.messageService.errorMessage('Tierliste konnte nicht geladen werden.', err);
            },
        });
    }

    private canRunAction(tierliste: Tierliste, options?: { requireReopenable?: boolean }): boolean {
        if (!tierliste?.tierliste_id) {
            return false;
        }
        if (options?.requireReopenable) {
            const tlStatus = Number(tierliste.status_id) || 0;
            return tlStatus === 162 && KieneUtils.isApiOne(tierliste.darf_wieder_oeffnen);
        }
        if (this.isImportierenMode) {
            const statusId = Number(tierliste.status_id) || 0;
            if (statusId !== 162) {
                return false;
            }
            return Number(tierliste.import_einstallen ?? 0) > 0;
        }
        if (this.isAusstallenMode) {
            return !isTierlisteClosed(tierliste.status_id);
        }
        return isTierlisteClosed(tierliste.status_id);
    }

    private bindTierliste(tierlisteId: number): void {
        this.loadSubscription?.unsubscribe();
        if (!tierlisteId || tierlisteId <= 0) {
            this.tierliste = null;
            this.loading = false;
            return;
        }
        if (this.tierliste?.tierliste_id === tierlisteId && !this.loading) {
            return;
        }
        this.loading = true;
        this.loadSubscription = this.menuCache.get(tierlisteId).subscribe({
            next: (tierliste: Tierliste) => {
                if (this.tierlisteId === tierlisteId) {
                    this.tierliste = tierliste;
                    this.loading = false;
                }
            },
            error: err => {
                if (this.tierlisteId === tierlisteId) {
                    this.tierliste = null;
                    this.loading = false;
                    this.messageService.errorMessage('Tierliste konnte nicht geladen werden.', err);
                }
            },
        });
    }

    private emitChanged(): void {
        this.changed.emit();
        if (!this.tierlisteId) {
            return;
        }
        this.menuCache.invalidate(this.tierlisteId);
        this.bindTierliste(this.tierlisteId);
    }
}
